/* eslint-disable camelcase */
import { store } from "../../redux/store";

/**
 * Returns the current order from the Redux store.
 *
 * @typedef {Object} Category
 * @property {string} id
 * @property {string} title
 * @property {string} slug
 * @property {number} parentId
 *
 * @typedef {Object} Product
 * @property {string} id
 * @property {string} title
 * @property {string} productImageSmall
 * @property {Category[]} categories
 * @property {boolean} hasAssortments
 * @property {number} price
 *
 * @typedef {Object} Item
 * @property {string} id
 * @property {number} quantity
 * @property {number} price
 * @property {Product} product
 * @property {null} boxOptions
 * @property {Array} productOptions
 *
 * @typedef {Object} ShippingAddress
 * @property {string} address1
 * @property {string} city
 * @property {string} state
 * @property {string} postcode
 *
 * @typedef {Object} ShippingMethod
 * @property {string} id
 * @property {string} title
 *
 * @typedef {Object} Total
 * @property {string} title
 * @property {number} value
 *
 * @typedef {Object} PaymentMethod
 * @property {string} id
 * @property {string} title
 * @property {string} description
 * @property {string} variables
 *
 * @typedef {Object} Variable
 * @property {null} key
 * @property {null} value
 *
 * @typedef {Object} StorePaymentMethod
 * @property {string} id
 * @property {number} paymentMethodId
 * @property {number} storeId
 * @property {boolean} active
 * @property {PaymentMethod} paymentMethod
 * @property {string} iconUrlPath
 * @property {boolean} isEmbeddedHtml
 * @property {null|string} embeddedJs
 * @property {null|string} embeddedHtml
 * @property {null|string} embeddedLink
 * @property {string} groupTitle
 * @property {string} uiComponentType
 * @property {null|string} buttonUrlPath
 * @property {number} theOrder
 * @property {(Variable|null)[]} variables
 *
 * @typedef {Object} Currency
 * @property {string} name
 * @property {string} code
 * @property {string} symbol
 *
 * @typedef {Object} Loyalty
 * @property {string} rewards
 *
 * @typedef {Object} Customer
 * @property {string} id
 * @property {Array} cards
 * @property {string} firstname
 * @property {string} lastname
 * @property {string} email
 * @property {string} telephone
 * @property {null} telephoneNew
 * @property {string} phoneTelephone
 * @property {string} prefixTelephone
 * @property {Loyalty} loyalty
 *
 * @typedef {Object} Store
 * @property {string} id
 * @property {string} address
 * @property {string} name
 * @property {string} pickupEstimate
 * @property {string} deliveryEstimate
 * @property {Currency} currency
 * @property {StorePaymentMethod[]} storePaymentMethods
 *
 * @typedef {Object} DeliveryStatus
 * @property {string} status
 * @property {null} lat
 * @property {null} lon
 * @property {null} driverFirstName
 * @property {null} driverName
 * @property {null} dropoffTime
 * @property {null} estimatedPickupTime
 * @property {null} estimatedPickupTimeMax
 * @property {string} estimatedDropoffTime
 * @property {string} estimatedDropoffTimeMax
 * @property {null} deliveryImageUrl
 * @property {null} deliveryServiceDeliveryId
 *
 * @typedef {Object} Order
 * @property {string} id
 * @property {string} code
 * @property {boolean} isContactlessDelivery
 * @property {string} invoiceDate
 * @property {boolean} isCancelled
 * @property {boolean} isRefunded
 * @property {boolean} isCooledDown
 * @property {boolean} isStale
 * @property {number} shippingMethodMinAmount
 * @property {string} deliveryTime
 * @property {string} orderMethod
 * @property {string} deliveryEstimate
 * @property {null} defaultPaymentCredential
 * @property {DeliveryStatus} deliveryStatus
 * @property {number} loyaltyMultiplier
 * @property {string} trackingID
 * @property {string} createdAt
 * @property {string} comment
 * @property {boolean} includeSpoon
 * @property {null} status
 * @property {Store} store
 * @property {Array} orderRedeemables
 * @property {boolean} useCookieDough
 * @property {Array} upsellProducts
 * @property {null} contactDetails
 * @property {boolean} isGift
 * @property {null} paymentMethod
 * @property {null} giftRecipientContactDetails
 * @property {null} sender
 * @property {null} recipient
 * @property {Customer} customer
 * @property {Item[]} items
 * @property {Array} discounts
 * @property {number} tax
 * @property {number} tip
 * @property {null} subtotal
 * @property {number} total
 * @property {number} priceWithTax
 * @property {Total[]} totals
 * @property {number} orderTypeId
 * @property {ShippingMethod} shippingMethod
 * @property {string} shippingAddress2
 * @property {ShippingAddress} shippingAddress
 * @property {Array} orderExperiences
 * @property {boolean} isBigOrder
 *
 * @returns {Order} The current order object.
 */
import ddLog from "./datadog/log";

function getCurrentOrder() {
  return store.getState().order;
}

/**
 * Hashes a string using SHA-256
 *
 * @param {string} data - The string to hash
 * @returns {Promise<string|null>} The hashed string
 */
async function sha256(data) {
  if (!data) {
    return null;
  }

  // Convert the data to a Uint8Array
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);

  // Hash the data
  const hashBuffer = await crypto.subtle.digest("SHA-256", dataBuffer);

  // Convert the hash to a hex string
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, "0")).join("");

  return hashHex;
}

const getCookie = name => {
  return (
    document.cookie
      ?.split("; ")
      ?.find(row => row.startsWith(`${name}=`))
      ?.split("=")[1] || ""
  );
};

async function getUserData(orderData = undefined) {
  const order = orderData ?? getCurrentOrder();

  return (
    [
      ["fbc", getCookie("_fbc")],
      ["fbp", getCookie("_fbp")],
      ["fn", await sha256(order?.customer?.firstname)],
      ["ln", await sha256(order?.customer?.lastname)],
      ["ct", await sha256(order?.shippingAddress?.city)],
      ["st", await sha256(order?.shippingAddress?.state)],
      ["zp", await sha256(order?.shippingAddress?.postcode)]
    ]
      // eslint-disable-next-line no-unused-vars
      .filter(([_, value]) => Boolean(value))
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {})
  );
}

async function getCustomOrderData(orderData = undefined) {
  const order = orderData ?? getCurrentOrder();

  return {
    currency: order?.store?.currency?.code ?? "USD",
    value:
      order?.totals?.find(t => t.title === "Total")?.value ?? order?.total ?? 0,
    content_type: "product",
    order_id: order?.id,
    external_id: await sha256(order?.customer_id),
    contents: order?.items?.map(item => ({
      id: item?.product?.id,
      quantity: item?.quantity,
      price: item?.product?.price,
      name: item?.product?.title
    }))
  };
}

export function getCurrentTimestamp() {
  return Date.now();
}

/**
 * @param {string} eventId - The ID of the event to send
 * @param {string} eventName - The name of the event to send
 * @param {Object} customData - Custom data to send with the event
 */
export async function enrichFacebookPayload(
  eventId,
  eventName,
  customData = undefined,
  order = undefined
) {
  try {
    const userData = await getUserData(order);
    const timestamp = getCurrentTimestamp();
    const payload = {
      event_id: eventId,
      user_data: userData,
      event_name: eventName,
      event_time: timestamp,
      action_source: "website",
      event_source_url: window?.location?.href,
      custom_data: customData ?? (await getCustomOrderData(order))
    };

    return payload;
  } catch (error) {
    ddLog.logError(error);
    return null;
  }
}

/**
 * Returns a unique event ID for a product based on the product ID and quantity.
 *
 * @param {string} productId - The ID of the product
 * @returns {string} The unique event ID
 */
export function getProductEventId(productId) {
  const order = getCurrentOrder();
  const item = order?.items?.find(i => i.product.id === productId);
  return `${order?.code ?? Date.now()}-${productId}-${item?.quantity ?? 1}`;
}
